import { useEffect, useId, useRef } from 'react';
import DeviceUtils from '../utils/DeviceUtils';

/**
 * Bridges an overlay's open/close state with the mobile app WebView.
 *
 * Message contract:
 *   Web → native: { action: 'overlayOpened', id }
 *   Web → native: { action: 'overlayClosed', id }
 *   Native → web: { action: 'closeOverlay' } or { action: 'closeOverlay', id }
 *
 * @param {boolean} visible - Whether the overlay is currently open
 * @param {Function} onClose - Called when the native app requests a close
 * @param {string} [id] - Stable id so native can target a specific overlay
 */
export function useMobileOverlayBridge(visible, onClose, id) {
    const generatedId = useId();
    const overlayId = id ?? generatedId;
    const wasVisibleRef = useRef(false);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!DeviceUtils.isMobileApp()) return;

        if (visible && !wasVisibleRef.current) {
            DeviceUtils.notifyOverlayOpened({ id: overlayId });
        } else if (!visible && wasVisibleRef.current) {
            DeviceUtils.notifyOverlayClosed({ id: overlayId });
        }
        wasVisibleRef.current = !!visible;

        return () => {
            if (wasVisibleRef.current) {
                DeviceUtils.notifyOverlayClosed({ id: overlayId });
                wasVisibleRef.current = false;
            }
        };
    }, [visible, overlayId]);

    useEffect(() => {
        if (!DeviceUtils.isMobileApp()) return;

        const handleMessage = (event) => {
            let data = event.data;
            try {
                data = typeof data === 'string' ? JSON.parse(data) : data;
            } catch {
                return;
            }

            if (data?.action === 'closeOverlay' && (!data.id || data.id === overlayId)) {
                onCloseRef.current?.();
            }
        };

        // iOS uses window; Android RN WebView often uses document
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [overlayId]);
}
